import {
  isDigitalKeyLocationItem,
  normalizeKeyLocationLabel,
} from "../services/propertyKeyLocation";
import { commissionFormFromApi, type PropertyCommissionForm } from "./propertyCommission";

export function resolveVisitKeyLocationName(
  property: unknown,
  appointmentKeyLocation?: unknown
): string {
  const fromProperty =
    property &&
    typeof property === "object" &&
    !Array.isArray(property) &&
    Object.keys(property as object).length > 0
      ? property
      : null;
  const record = fromProperty as {
    key_location_name?: unknown;
    key_location?: unknown;
  } | null;

  return (
    normalizeKeyLocationLabel(record?.key_location_name) ||
    normalizeKeyLocationLabel(record?.key_location) ||
    normalizeKeyLocationLabel(appointmentKeyLocation) ||
    "—"
  );
}

export function resolveVisitDigitalKeyPassword(property: unknown): string | null {
  if (!property || typeof property !== "object" || Array.isArray(property)) {
    return null;
  }

  const password = (property as { key_location_password?: unknown })
    .key_location_password;
  if (typeof password !== "string") return null;

  const trimmed = password.trim();
  return trimmed === "" ? null : trimmed;
}

/** Plain-text password from property detail when the location is digital. */
export function readPropertyKeyLocationPassword(property: unknown): string {
  return resolveVisitDigitalKeyPassword(property) ?? "";
}

/** Listing sends `key_location_password` only when `key_location.slug` is digital. */
export function readListingKeyLocationPassword(property: unknown): string {
  if (!property || typeof property !== "object" || Array.isArray(property)) {
    return "";
  }
  const record = property as Record<string, unknown>;
  const location = record.key_location;
  if (
    location &&
    typeof location === "object" &&
    !Array.isArray(location) &&
    !isDigitalKeyLocationItem(location as { slug?: string; name?: string })
  ) {
    return "";
  }
  return readPropertyKeyLocationPassword(record);
}

export function readPropertyRecordFromAdmGet(
  payload: unknown
): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const root = payload as Record<string, unknown>;
  const data = root.data;

  if (data && typeof data === "object" && !Array.isArray(data)) {
    const nested = (data as Record<string, unknown>).property;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
    return data as Record<string, unknown>;
  }

  if (root.property && typeof root.property === "object" && !Array.isArray(root.property)) {
    return root.property as Record<string, unknown>;
  }

  return root;
}

export async function fetchPropertyKeyLocationPassword(
  client: { get: (url: string) => Promise<{ data: unknown }> },
  propertyId: number
): Promise<string> {
  const response = await client.get(`/api/adm/properties/${propertyId}`);
  return readPropertyKeyLocationPassword(
    readPropertyRecordFromAdmGet(response.data)
  );
}

export async function fetchPropertyCommissionForm(
  client: { get: (url: string) => Promise<{ data: unknown }> },
  propertyId: number
): Promise<PropertyCommissionForm> {
  const response = await client.get(`/api/adm/properties/${propertyId}`);
  return commissionFormFromApi(
    readPropertyRecordFromAdmGet(response.data) ?? {}
  );
}
